import { definedMap } from "@glideapps/ts-necessities";

import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import {
    PropertyDescription,
    ArrayContentDescription,
    PropertyKind,
    getPrimitiveNonHiddenColumns,
    ArrayScreenFormat,
    getTableColumn,
    getTableColumnDisplayName,
    MutatingScreenKind,
    TableGlideType,
} from "../description";
import {
    PropertyDescriptor,
    PropertyTableGetter,
    ComponentBuilder,
    makeStringyColumnPropertyDescriptor,
    ComponentSpecialCaseDescriptor,
    ArrayPropertyStyle,
    ComponentConfiguratorContext,
    ActionPropertyDescriptor,
} from "../components/index";
import {
    getColumnProperty,
    makeColumnProperty,
    makeArrayProperty,
    getArrayProperty,
} from "../components/property-utils";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { ArrayContentHandlerBase, FixedArrayContent, InlineListBuild } from "./array-content";
import { PropertySection } from "../components/property-sections";
import { SwitchPropertyHandler, EnumPropertyHandler } from "../components/property-handlers";
import { ColorTheme } from "../components/color-types";
import { ChartType } from "../components/chart-types";
import { mapFilterUndefined, defined } from "../../Support";
import { compileDisplayFormula, withColumnFormat, getPropertyWithColumnFormat } from "../formulas";
import { makeText } from "../builder-utils";

const showTotalInCenterPropertyHandler = new SwitchPropertyHandler(
    { showTotalInCenter: true },
    "Show total in center",
    PropertySection.Design
);

const showLegendByDefaultPropertyHandler = new SwitchPropertyHandler(
    { showLegendByDefault: true },
    "Show legend by default",
    PropertySection.Design
);

const showLabelsOnBarsPropertyHandler = new SwitchPropertyHandler(
    { showLabelsOnBars: true },
    "Show labels on bars",
    PropertySection.Design
);

const colorThemePropertyHandler = new EnumPropertyHandler(
    { colorTheme: ColorTheme.Barragan },
    "Color Theme",
    "Color Theme",
    [
        {
            value: ColorTheme.Ansel,
            label: "Ansel",
            icon: "ansel",
        },
        {
            value: ColorTheme.Barragan,
            label: "Barragán",
            icon: "barragan",
        },
        {
            value: ColorTheme.Hockney,
            label: "Hockney",
            icon: "hockney",
        },
        {
            value: ColorTheme.Kahlo,
            label: "Kahlo",
            icon: "kahlo",
        },
        {
            value: ColorTheme.Magritte,
            label: "Magritte",
            icon: "magritte",
        },
        {
            value: ColorTheme.Missy,
            label: "Missy",
            icon: "missy",
        },
        {
            value: ColorTheme.Pamplemousse,
            label: "Pamplemousse",
            icon: "pamplemousse",
        },
        {
            value: ColorTheme.Quinquela,
            label: "Quinquela",
            icon: "quinquela",
        },
        {
            value: ColorTheme.RuPaul,
            label: "RuPaul",
            icon: "rupaul",
        },
        {
            value: ColorTheme.Turrell,
            label: "Turrell",
            icon: "turrell",
        },
        {
            value: ColorTheme.Vermeer,
            label: "Vermeer",
            icon: "vermeer",
        },
    ],

    PropertySection.Design,
    "dropdown"
);

const stylePropertyHandler = new EnumPropertyHandler(
    { style: ChartType.DonutChart },
    "Style",
    "Style",
    [
        {
            value: ChartType.DonutChart,
            label: "Donut",
            icon: "donutChart",
        },
        {
            value: ChartType.PieChart,
            label: "Pie",
            icon: "pieChart",
        },
        { value: ChartType.BarChart, label: "Bar", icon: "barChart" },
        { value: ChartType.StackedBarChart, label: "Stack", icon: "stackedBarChart" },
    ],

    PropertySection.Style,
    "large-images"
);

export interface SeriesDescription {
    readonly valueProperty: PropertyDescription;
}

interface PieChartArrayContentDescription extends ArrayContentDescription {
    readonly style: PropertyDescription;
    readonly titleString: PropertyDescription | undefined;
    readonly captionString: PropertyDescription | undefined;
    readonly unitsString: PropertyDescription | undefined;
    readonly labelProperty: PropertyDescription;
    readonly valueProperty: PropertyDescription | undefined; // only for donut/pie
    readonly series: PropertyDescription | undefined; // only for bar
    readonly showTotalInCenter: PropertyDescription;
    readonly showLegendByDefault: PropertyDescription;
}

function getChartType(desc: PieChartArrayContentDescription | undefined): ChartType {
    return definedMap(desc, d => stylePropertyHandler.getEnum(d)) ?? ChartType.DonutChart;
}

export class PieChartArrayContentHandler extends ArrayContentHandlerBase<PieChartArrayContentDescription> {
    constructor() {
        super(ArrayScreenFormat.PieChart);
    }

    public get specialCaseDescriptor(): ComponentSpecialCaseDescriptor {
        return {
            name: "Charts",
            analyticsName: "chart",
            description: "A colorful chart",
            img: "componentCharts",
            group: "Charts",
            isNew: false,
            appKinds: AppKind.App,
        };
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: PieChartArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        _searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined
    ): SerializedComponent {
        const { b } = builder;

        const labelColumn = getColumnProperty(desc.labelProperty);
        if (labelColumn === undefined) return b.emptyUI;

        const showTotal = showTotalInCenterPropertyHandler.getSwitch(desc);
        const showLegendByDefault = b.constant(showLegendByDefaultPropertyHandler.getSwitch(desc));
        const showLabelsOnBars = b.constant(showLabelsOnBarsPropertyHandler.getSwitch(desc));
        const color = b.constant(colorThemePropertyHandler.getEnum(desc));
        const style = stylePropertyHandler.getEnum(desc);
        const isBar = style === ChartType.BarChart;

        let valueColumnNames: readonly string[] | undefined;
        if (isBar) {
            const arr = getArrayProperty<SeriesDescription>(desc.series);
            if (arr === undefined) return b.emptyUI;
            valueColumnNames = mapFilterUndefined(arr, s => getColumnProperty(s.valueProperty));
        } else {
            const columnName = getColumnProperty(desc.valueProperty);
            if (columnName !== undefined) {
                valueColumnNames = [columnName];
            }
        }
        if (valueColumnNames !== undefined) {
            valueColumnNames = Array.from(new Set(valueColumnNames));
        }

        const valueColumns = definedMap(valueColumnNames, vcn =>
            mapFilterUndefined(vcn, n => getTableColumn(table, n))
        );

        const grouped = b.group(arrayData, getPropertyWithColumnFormat(builder, table, labelColumn));
        const items = b.getNonTableProperty("items", b.getContext);
        let valueMappers: readonly SerializedComponent[];
        if (valueColumns !== undefined && valueColumns.length > 0) {
            valueMappers = valueColumns.map(c => {
                const v = b.primitive("Sum", { number: b.getProperty(c.name, b.getContext, table), list: items });
                return b.let(
                    [["value", v]],
                    b.list([b.getVariable("value"), withColumnFormat(builder, b.getVariable("value"), table, c)])
                );
            });
        } else {
            valueMappers = [b.listLength(items)];
        }

        const { inputContext: screenContext, tables: screenTables } = defined(inlineListBuild);
        const title = makeText(builder, desc.titleString, screenTables.input, true, true, false, false, screenContext);
        const caption = makeText(
            builder,
            desc.captionString,
            screenTables.input,
            true,
            true,
            true,
            false,
            screenContext
        );

        if (getChartType(desc) === ChartType.StackedBarChart) {
            return b.ui("StackedBarChart", {
                list: b.map(
                    grouped,
                    b.object({
                        label: b.getNonTableProperty("key", b.getContext),
                        value: defined(valueMappers[0]),
                    })
                ),
                title,
                caption,
                showLegend: showLegendByDefault,
                showLabels: showLabelsOnBars,
                color,
            });
        } else if (getChartType(desc) === ChartType.BarChart) {
            const seriesLabels = valueColumns?.map(getTableColumnDisplayName);
            return b.ui("BarChart", {
                list: b.map(
                    grouped,
                    b.object({
                        label: b.getNonTableProperty("key", b.getContext),
                        values: b.list(valueMappers),
                    })
                ),
                title,
                caption,
                showLegend: showLegendByDefault,
                showLabels: showLabelsOnBars,
                seriesLabels: b.constant(seriesLabels),
                color,
                style: b.constant(style),
            });
        } else {
            let total: SerializedComponent | undefined;

            if (showTotal) {
                let getValue = b.getNonTableProperty("value", b.getContext);
                if (valueColumns !== undefined) {
                    getValue = b.listNth(getValue, 0);
                }
                total = b.sum(b.getVariable("list"), getValue);
                if (valueColumns !== undefined) {
                    total = compileDisplayFormula(builder, total, table, valueColumns[0]) ?? total;
                }
            }
            return b.let(
                [
                    [
                        "list",
                        b.map(
                            grouped,
                            b.object({
                                label: b.getNonTableProperty("key", b.getContext),
                                value: defined(valueMappers[0]),
                            })
                        ),
                    ],
                ],
                b.ui("PieChart", {
                    list: b.getVariable("list"),
                    title,
                    caption,
                    units: makeText(builder, desc.unitsString, table, true),
                    total,
                    showLegend: showLegendByDefault,
                    color,
                    style: b.constant(style),
                })
            );
        }
    }

    public getNeedsOrder(desc: PieChartArrayContentDescription | undefined): boolean {
        return getChartType(desc) === ChartType.BarChart;
    }

    public getContentPropertyDescriptors<T extends PieChartArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean
    ): readonly PropertyDescriptor[] {
        const chartType = getChartType(desc);
        const isBar = chartType === ChartType.BarChart;
        const isDonut = chartType === ChartType.DonutChart;

        const valuePropertyDescriptor: PropertyDescriptor = {
            kind: PropertyKind.Column,
            property: { name: "valueProperty" },
            section: PropertySection.Data,
            label: "Quantity",
            required: isBar,
            editable: true,
            searchable: false,
            emptyByDefault: isDonut,
            getIndirectTable: getPropertyTable,
            getAllowedColumns: getPrimitiveNonHiddenColumns,
            preferredNames: ["value", "quantity"],
            preferredType: "number",
        };
        const valueDescriptors: PropertyDescriptor[] = [];
        if (isBar) {
            valueDescriptors.push({
                kind: PropertyKind.Array,
                label: "Series",
                property: { name: "series" },
                section: PropertySection.Data,
                properties: [valuePropertyDescriptor],
                allowEmpty: true,
                allowReorder: true,
                addItemLabel: "Add quantity",
                style: ArrayPropertyStyle.Default,
            });
        } else {
            valueDescriptors.push(valuePropertyDescriptor);
        }

        const result: PropertyDescriptor[] = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                undefined
            ),
            makeStringyColumnPropertyDescriptor(
                "labelProperty",
                "Label",
                ["required", "editable"],
                PropertySection.Data,
                getPropertyTable,
                ["label", "name"]
            ),
            makeTextPropertyDescriptor("titleString", "Title", "Enter title", false, mutatingScreenKind, {
                propertySection: PropertySection.Top,
            }),
            makeTextPropertyDescriptor("captionString", "Caption", "Enter caption", false, mutatingScreenKind, {
                propertySection: PropertySection.Top,
            }),
            stylePropertyHandler,
            ...valueDescriptors,
            colorThemePropertyHandler,
            showLegendByDefaultPropertyHandler,
        ];

        if (chartType === ChartType.DonutChart) {
            result.push(
                makeTextPropertyDescriptor("unitsString", "Units", "Enter units", false, mutatingScreenKind, {})
            );
        }
        if (chartType === ChartType.DonutChart) {
            result.push(showTotalInCenterPropertyHandler);
        }
        if (chartType === ChartType.BarChart) {
            result.push(showLabelsOnBarsPropertyHandler);
        }
        return result;
    }

    public getActionDescriptors<T extends PieChartArrayContentDescription>(): readonly ActionPropertyDescriptor<T>[] {
        return [];
    }

    public getBasicSearchProperties(): readonly string[] {
        return [];
    }

    public needsCaption(): PropertySection | undefined {
        return undefined;
    }

    public fixContentDescription(chartContent: PieChartArrayContentDescription): FixedArrayContent | undefined {
        const style = stylePropertyHandler.getEnum(chartContent);
        if (style !== ChartType.BarChart) return undefined;

        if (chartContent.series !== undefined) return undefined;

        const valueColumn = getColumnProperty(chartContent.valueProperty);
        if (valueColumn === undefined) return undefined;

        const series: SeriesDescription[] = [
            {
                valueProperty: makeColumnProperty(valueColumn),
            },
        ];

        return {
            properties: { ...chartContent, series: makeArrayProperty(series) } as PieChartArrayContentDescription,
            format: ArrayScreenFormat.PieChart,
        };
    }
}
